import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Chip, Icon } from "@legal-500/legal500-design-system";
import type { Column, ToastType } from "@legal-500/legal500-design-system";
import LoadingState from "@/components/LoadingState";
import { DataTable } from "@/components/DataTable/DataTable";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import { campaignsService, type Segment, type SegmentWithProgress } from "@/api/campaigns.service";
import { SegmentTargetsModal } from "./SegmentTargetsModal";
import { ScheduleSegmentModal } from "./ScheduleSegmentModal";
import {
  segmentDisplayChip,
  segmentProgressLabel,
  segmentRunSkipped,
  segmentScheduleDisplay,
  segmentSkippedHint,
} from "./campaign-display";
import { ROUTES } from "@/routes";
import styles from "./MailingGroups.module.css";

type Props = {
  campaignId: string;
  refreshKey: number;
  onToast: (message: string, type: ToastType) => void;
  onCountChange?: (count: number) => void;
  // When the campaign has no template assigned, dispatch (Send now / Schedule) is
  // blocked — a send with no template has nothing to render. Creating drafts is
  // still allowed.
  templateMissing?: boolean;
  templateName?: string | null;
};

const NO_TEMPLATE_HINT = "Assign a template to this campaign first";

type ViewingTargets = { segmentId: string; label: string };

// A segment is "in flight" while the send worker is dispatching it — either the
// segment status flipped to `sending`, or its run is actively `running`. The
// list polls only while at least one segment is in flight.
const isInFlight = (s: Segment): boolean => s.status === "sending" || s.run?.status === "running";

const POLL_INTERVAL_MS = 2000;

// The browser's IANA zone is sent as a scheduling hint (arch-discussion §14.2);
// it is not a constraint — the backend stores and schedules the absolute UTC.
const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

export function SegmentsList({
  campaignId,
  refreshKey,
  onToast,
  onCountChange,
  templateMissing = false,
  templateName,
}: Props) {
  const navigate = useNavigate();
  const {
    publicationId = "",
    edition = "",
    groupId = "",
  } = useParams<{ publicationId: string; edition: string; groupId: string }>();

  const [segments, setSegments] = useState<SegmentWithProgress[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [viewing, setViewing] = useState<ViewingTargets | null>(null);
  const [scheduling, setScheduling] = useState<Segment | null>(null);
  const [confirmingSend, setConfirmingSend] = useState<Segment | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Lightweight refetch used by both the initial load and the poll: refreshes
  // only the segment records (their live `run` progress), never the per-row
  // target counts, which are frozen once the send is created.
  const refreshSegments = useCallback(async (): Promise<SegmentWithProgress[]> => {
    const list = await campaignsService.listSegments(campaignId);
    setSegments(list);
    return list;
  }, [campaignId]);

  const load = useCallback(() => {
    let cancelled = false;
    setError(null);
    refreshSegments()
      .then(async list => {
        if (cancelled) return;
        // Target counts are not on the list payload; fetch them lazily per row.
        const entries = await Promise.all(
          list.map(async s => {
            try {
              const { total } = await campaignsService.listTargets(campaignId, s.id);
              return [s.id, total] as const;
            } catch {
              return [s.id, -1] as const;
            }
          }),
        );
        if (!cancelled) setCounts(Object.fromEntries(entries));
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load segments.");
      });
    return () => {
      cancelled = true;
    };
  }, [campaignId, refreshSegments]);

  useEffect(() => load(), [load, refreshKey]);

  // Lift the send count up so the parent's "Sends" tab badge stays in sync.
  useEffect(() => {
    if (segments) onCountChange?.(segments.length);
  }, [segments, onCountChange]);

  // Poll every ~2s while any send is dispatching, so the progress counters tick
  // live; stop as soon as none are in flight (transient poll errors are swallowed
  // so a blip doesn't wipe the last good data).
  const anyInFlight = segments?.some(isInFlight) ?? false;
  useEffect(() => {
    if (!anyInFlight) return;
    const interval = setInterval(() => {
      // Swallow transient poll errors so a blip doesn't wipe the last good data.
      refreshSegments().catch(() => undefined);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [anyInFlight, refreshSegments]);

  const handleDelete = async (segmentId: string) => {
    setBusyId(segmentId);
    try {
      await campaignsService.deleteSegment(campaignId, segmentId);
      onToast("Segment deleted.", "success");
      load();
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 409) {
        onToast("Only draft segments can be deleted.", "error");
      } else {
        onToast(err instanceof Error ? err.message : "Failed to delete segment.", "error");
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleCancel = async (segment: Segment) => {
    setBusyId(segment.id);
    try {
      await campaignsService.cancelSegment(campaignId, segment.id);
      onToast("Send canceled.", "success");
      await refreshSegments();
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 409) {
        onToast("This send can no longer be canceled — it is already sending or sent.", "error");
      } else {
        onToast(err instanceof Error ? err.message : "Failed to cancel send.", "error");
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleScheduled = (segment: Segment) => {
    setScheduling(null);
    onToast(segment.scheduled_at ? "Send scheduled." : "Send queued to go out now.", "success");
    void refreshSegments();
  };

  const handleScheduleError = (message: string) => {
    setScheduling(null);
    onToast(message || "This send can no longer be scheduled.", "error");
    void refreshSegments();
  };

  // Send-now dispatches immediately (scheduledAt=null). Errors are left to throw
  // so the ConfirmationModal (which awaits onConfirm) surfaces them inline.
  const handleSendNow = async () => {
    if (!confirmingSend) return;
    await campaignsService.scheduleSegment(campaignId, confirmingSend.id, {
      scheduledAt: null,
      timezone: BROWSER_TZ,
    });
    onToast("Sending now.", "success");
    setConfirmingSend(null);
    await refreshSegments();
  };

  const columns: Column<SegmentWithProgress>[] = [
    { header: "Label", accessor: "label", render: v => (v as string | null) ?? "—" },
    {
      header: "Status",
      accessor: "status",
      render: (_v, row) => {
        const chip = segmentDisplayChip(row);
        return (
          <Chip
            size="small"
            color={chip.color}
            className={chip.color === "default" ? styles.neutralStatusChip : undefined}
          >
            <span className={styles.statusChip}>
              <span
                className={styles.statusDot}
                style={{ backgroundColor: chip.dotColor }}
                aria-hidden="true"
              />
              {chip.label}
            </span>
          </Chip>
        );
      },
    },
    {
      header: "Scheduled at",
      accessor: "scheduled_at",
      render: (_v, row) => {
        const display = segmentScheduleDisplay(row);
        if (display.variant === "muted") {
          return <span className={styles.scheduleMuted}>{display.text}</span>;
        }
        if (display.variant === "immediate") {
          return <span className={styles.scheduleImmediate}>{display.text}</span>;
        }
        return (
          <span>
            {display.text}
            {display.relative && (
              <span className={styles.scheduleRelative}>{display.relative}</span>
            )}
          </span>
        );
      },
    },
    {
      header: "Progress",
      accessor: "id",
      render: (_v, row) => {
        const progress = segmentProgressLabel(row.counts);
        if (!progress) return <span className={styles.mutedNote}>—</span>;
        const run = row.run;
        // Targets a completed run never touched = already claimed by an earlier
        // send in this campaign (per-campaign dedup); 0 while the run is going.
        const skipped = run ? segmentRunSkipped(run) : 0;
        return (
          <div className={styles.progressCell}>
            <span className={styles.progressCount}>{progress}</span>
            {run && run.failed > 0 && (
              <span className={`${styles.progressBadge} ${styles.progressBadgeError}`}>
                {run.failed.toLocaleString()} failed
              </span>
            )}
            {run && run.suppressed > 0 && (
              <span className={styles.progressBadge}>
                {run.suppressed.toLocaleString()} suppressed
              </span>
            )}
            {run && skipped > 0 && (
              <span className={styles.progressBadge} title={segmentSkippedHint(run)}>
                {skipped.toLocaleString()} skipped
              </span>
            )}
          </div>
        );
      },
    },
    {
      header: "",
      accessor: "id",
      render: (_v, row) => {
        const targetCount = counts[row.id];
        const detailsButton = (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() =>
              navigate(
                ROUTES.surveys.campaignSendDetail(
                  publicationId,
                  edition,
                  groupId,
                  campaignId,
                  row.id,
                ),
              )
            }
          >
            Details
          </Button>
        );
        // A send with no frozen targets has nothing to dispatch; block send-now
        // and scheduling until at least one recipient is selected.
        const noTargets = targetCount !== undefined && targetCount <= 0;
        const targetsButton = (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => setViewing({ segmentId: row.id, label: row.label ?? row.id })}
          >
            Targets
          </Button>
        );
        return (
          <div className={styles.campaignsActions}>
            {detailsButton}
            {row.status === "draft" && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={busyId === row.id || noTargets || templateMissing}
                  title={templateMissing ? NO_TEMPLATE_HINT : undefined}
                  onClick={() => setConfirmingSend(row)}
                >
                  Send now
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  disabled={busyId === row.id || noTargets || templateMissing}
                  title={templateMissing ? NO_TEMPLATE_HINT : undefined}
                  onClick={() => setScheduling(row)}
                >
                  Schedule…
                </Button>
                {targetsButton}
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<Icon icon="delete" size="small" />}
                  disabled={busyId === row.id}
                  onClick={() => void handleDelete(row.id)}
                >
                  Delete
                </Button>
              </>
            )}
            {row.status === "scheduled" && (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  disabled={busyId === row.id}
                  onClick={() => setScheduling(row)}
                >
                  Reschedule
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  disabled={busyId === row.id}
                  onClick={() => void handleCancel(row)}
                >
                  {busyId === row.id ? "Canceling…" : "Cancel"}
                </Button>
                {targetsButton}
              </>
            )}
            {(row.status === "sending" || row.status === "sent" || row.status === "canceled") &&
              targetsButton}
          </div>
        );
      },
    },
  ];

  return (
    <section className={styles.campaignsSection}>
      <h2 className={styles.sectionHeading}>Sends</h2>

      {error && <p className={styles.mutedNote}>Sends unavailable: {error}</p>}

      {!segments && !error ? (
        <LoadingState message="Loading sends..." />
      ) : (
        <div className={styles.tableContainer}>
          <DataTable
            data={segments ?? []}
            columns={columns}
            rowKey="id"
            emptyMessage="No segments yet."
          />
        </div>
      )}

      {viewing && (
        <SegmentTargetsModal
          campaignId={campaignId}
          segmentId={viewing.segmentId}
          label={viewing.label}
          onClose={() => setViewing(null)}
        />
      )}

      {scheduling && (
        <ScheduleSegmentModal
          campaignId={campaignId}
          segment={scheduling}
          onClose={() => setScheduling(null)}
          onScheduled={handleScheduled}
          onError={handleScheduleError}
        />
      )}

      {confirmingSend && (
        <ConfirmationModal
          title="Send now"
          message={`Send to ${counts[confirmingSend.id] ?? 0} recipients now?${
            templateName ? ` Template: ${templateName}.` : ""
          }`}
          warningMessage="This dispatches immediately and can't be undone."
          confirmButtonText="Send now"
          cancelButtonText="Cancel"
          onClose={() => setConfirmingSend(null)}
          onConfirm={handleSendNow}
        />
      )}
    </section>
  );
}
